import React, { Fragment, useCallback } from 'react';
import { View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { styled, useResponsiveValue } from 'dripsy';
import { observer } from 'mobx-react-lite';
import { useNavigation, useRoute } from '@react-navigation/native';

import {
  screens,
  TabNavigatorNavigationPropType,
  TabNavigatorRoutePropType,
} from '@navigation';
import {
  HorizontalList,
  ScrollView,
  Separator,
  // TrackItem,
  TutorialItem,
  ScreenTopBar,
  UserItem,
  EmptyResultView,
  EmptyResultTypeEnum,
  Loader,
} from '@components';
import { useAsyncInit, useSearch } from '@hooks';
import { useSearchModel } from '@models/Search/SearchModel';
import type { Tutorial } from '@models/Tutorials/TutorialModel';
import type { User } from '@models/Viewer/UserModel';
import { passIf } from '@utils';
// import type { Track } from '@models/Tracks/TrackModel';

const Container = styled(View)({
  flex: 1,
  bg: '$background0',
});

const VerticalSeparator = styled(View)({
  pr: ['$6', '$8'],
});

const LoaderContainer = styled(View)({
  flex: 1,
  alignItems: 'center',
  justifyContent: 'center',
});

const EmptyViewContainer = styled(LoaderContainer)({});

type NavigationPropType = TabNavigatorNavigationPropType<'Search'>;
type RoutePropType = TabNavigatorRoutePropType<'Search'>;

export const SearchScreen = observer(() => {
  const { t } = useTranslation();
  const { params } = useRoute<RoutePropType>();
  const nav = useNavigation<NavigationPropType>();
  const search = useSearchModel();
  const { performFetch, updateSearchQuery, store } = useSearch();
  const tutorialItemWidth = useResponsiveValue([253, 287]);
  const userItemWidth = useResponsiveValue([106, 184]);
  // const trackItemWidth = useResponsiveValue([141, 184]);

  useAsyncInit(() => fetch(), [params?.query]);

  async function fetch() {
    await performFetch(search.fetch.run);
  }

  const navigateToTutorial = useCallback((item: Tutorial) => {
    nav.push(screens.HubNavigator, {
      screen: screens.Tutorial,
      params: {
        id: item.id,
      },
    });
  }, []);

  const navigateToUser = useCallback((item: User) => {
    nav.navigate(screens.MusicNavigator, {
      screen: screens.UserProfileTabNavigator,
      params: {
        id: item.id,
      },
    });
  }, []);

  // TODO: Add navigation when tracks are implemented
  // const navigateToTrack = useCallback((item: Track) => {
  //   nav.navigate(screens.SearchNavigator, {
  //     screen: screens.Track,
  //     params: { id: item.id },
  //   });
  // }, []);

  function navigateToTutorialResults() {
    nav.navigate(screens.SearchNavigator, {
      screen: screens.SearchTutorial,
      params,
    });
  }

  function navigateToProfileResults() {
    nav.navigate(screens.SearchNavigator, {
      screen: screens.SearchProfile,
      params,
    });
  }

  // TODO: Add navigation when tracks are implemented
  // function navigateToTracksResults() {}

  const emptyResultType = search.fetch.isError
    ? EmptyResultTypeEnum.Error
    : EmptyResultTypeEnum.NoResult;

  const isNoResult = emptyResultType === EmptyResultTypeEnum.NoResult;

  const isEmptyView =
    !search.fetch.inProgress &&
    search.fetch.hasEverBeenRun &&
    search.isEmpty;

  const hasVideos = !search.videos.isEmpty;
  const hasUsers = !search.users.isEmpty;

  return (
    <Container>
      <ScreenTopBar
        title={t('search.title')}
        initSearchText={params?.query}
        onSearchChange={updateSearchQuery}
      />

      <Separator size="$8" />

      {search.fetch.inProgress && (
        <LoaderContainer>
          <Loader size="large" />
        </LoaderContainer>
      )}

      {isEmptyView && (
        <EmptyViewContainer>
          <EmptyResultView
            type={emptyResultType}
            description={passIf(
              isNoResult,
              t('search.emptySearchResult'),
            )}
          />
        </EmptyViewContainer>
      )}

      {!search.fetch.inProgress && !search.isEmpty && (
        <ScrollView>
          {hasVideos && (
            <HorizontalList
              title={t('search.video')}
              data={search.videos.asArray}
              onMorePress={navigateToTutorialResults}
              renderItem={({ item }) => (
                <TutorialItem
                  extraStore={store}
                  item={item}
                  width={tutorialItemWidth}
                  onPress={navigateToTutorial}
                />
              )}
              keyExtractor={(i) => i.id.toString()}
              ItemSeparatorComponent={VerticalSeparator}
            />
          )}

          {hasUsers && (
            <Fragment>
              <Separator size="$9" />
              <HorizontalList
                title={t('search.profiles')}
                data={search.users.asArray}
                onMorePress={navigateToProfileResults}
                renderItem={({ item }) => (
                  <UserItem
                    item={item}
                    width={userItemWidth}
                    onPress={navigateToUser}
                  />
                )}
                keyExtractor={(i) => i.id.toString()}
                ItemSeparatorComponent={VerticalSeparator}
              />
            </Fragment>
          )}

          {/* TODO: Add Track Search List */}
          <Separator size="$9" />
        </ScrollView>
      )}
    </Container>
  );
});
